/* eslint-disable react-hooks/exhaustive-deps */
import React, { MouseEventHandler, useEffect, useState } from "react"
import { Button } from "react-bootstrap"
import PasswordStrengthBar from "react-password-strength-bar"
import { useDispatch } from "react-redux"
import zxcvbn from "zxcvbn"
import { AppDispatch } from "../.."
import AlertComponent from "../../components/AlertComponent"
import TextInput from "../../components/InputWrapper/TextInput"
import AuthService from "../../service/auth/authService"
import { TApiResponse } from "../../service/TApiResponse"
import { showMasterRegisterToast } from "../../components/CustomToast"
import { パスワード強度 } from "../../constant/constant"
import {
  clearAlert,
  setAlerts,
  setWarningAlerts,
} from "../../globalStore/alertStore"
import { TAuthChangePasswordPutReq } from "../../service/auth/TAuth"
import Ajv, { ErrorObject, JSONSchemaType } from "ajv"
import util from "../../ts/util"
import ajvErrors from "ajv-errors"

type T入力 = TAuthChangePasswordPutReq & {
  新パスワード_確認: string
}

export const 入力schema: JSONSchemaType<T入力> = {
  type: "object",
  properties: {
    現パスワード: {
      type: "string",
      title: "現パスワード",
      minLength: 1,
      errorMessage: util.stringErrorMessage("現パスワード", {
        minLength: 1,
      }),
    },
    新パスワード: {
      type: "string",
      title: "新パスワード",
      minLength: 1,
      errorMessage: util.stringErrorMessage("新パスワード", {
        minLength: 1,
      }),
    },
    新パスワード_確認: {
      type: "string",
      title: "新パスワード_確認",
      minLength: 1,
      errorMessage: util.stringErrorMessage("新しいパスワード(確認)", {
        minLength: 1,
      }),
    },
  },
  required: ["新パスワード", "現パスワード", "新パスワード_確認"],
} as const

const Password = () => {
  //#region [state]
  const [新パスワード, set新パスワード] = useState("")
  const [現パスワード, set現パスワード] = useState("")
  const [新パスワード_確認, setパスワード_確認] = useState("")
  const [isInvalid現パスワード, setIsInvalid現パスワード] = useState<boolean>()
  const [isInvalid新パスワード, setIsInvalid新パスワード] = useState<boolean>()
  const [isInvalid新パスワード_確認, setIsInvalid新パスワード_確認] =
    useState<boolean>()
  const [passwordScore, setPasswordScore] = useState<number>()

  const dispatch: AppDispatch = useDispatch()
  //#endregion

  //#region [method]
  const submit: MouseEventHandler<HTMLElement> = async (event) => {
    dispatch(clearAlert())
    setIsInvalid現パスワード(false)
    setIsInvalid新パスワード(false)
    setIsInvalid新パスワード_確認(false)
    if (!isValidationCheckBeforeSubmit()) return

    if (新パスワード !== 新パスワード_確認) {
      dispatch(setWarningAlerts(["新しいパスワードが一致していません。"]))
      setIsInvalid新パスワード(true)
      setIsInvalid新パスワード_確認(true)
      return
    }

    try {
      const authService = new AuthService()
      const res: TApiResponse<Boolean> = await authService.changePassword({
        新パスワード,
        現パスワード,
      })
      dispatch(setAlerts(res))
      showMasterRegisterToast("パスワードを更新しました")
      set新パスワード("")
      set現パスワード("")
      setパスワード_確認("")
    } catch (err) {
      //errorメッセージ表示
      dispatch(setAlerts(err))
    }
  }

  const isValidationCheckBeforeSubmit = (): boolean => {
    // アラートの解除
    dispatch(clearAlert())

    const ajv = new Ajv({ allErrors: true })
    ajvErrors(ajv)

    const validate = ajv.compile(入力schema)

    const params: T入力 = {
      新パスワード,
      新パスワード_確認,
      現パスワード,
    }
    const validateResult = validate(params)

    if (!validateResult) {
      const errors = validate.errors
      if (errors != null && errors.length > 0) {
        //エラーメッセージのparamsより対象の項目を発見し色変え
        setValidateError(errors)
        dispatch(
          setWarningAlerts([
            ...errors
              .filter((e) => e.message != null)
              .map((e) => e.message ?? ""),
          ])
        )
      }
      return false
    }
    //TODO:強度が達していないときにエラーとするかは要検討
    return true
  }

  /**
   * エラー
   * @param errors
   */
  const setValidateError = (
    errors: ErrorObject<string, Record<string, any>, unknown>[]
  ) => {
    const instancePaths = errors.map((e) => e.instancePath.replaceAll("/", ""))
    setIsInvalid現パスワード(instancePaths.includes("現パスワード"))
    setIsInvalid新パスワード(instancePaths.includes("新パスワード"))
    setIsInvalid新パスワード_確認(instancePaths.includes("新パスワード_確認"))
  }

  //#endregion

  //#region
  useEffect(() => {
    const result = zxcvbn(新パスワード)
    setPasswordScore(result.score)
  }, [新パスワード])

  //#endregion

  return (
    <>
      <AlertComponent className="w-100"></AlertComponent>
      <form style={{ paddingTop: "43px" }}>
        <div
          style={{
            marginLeft: "26px",
            marginTop: "",
            width: "fit-content",
          }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "bold",
            }}>
            パスワードの変更
          </div>
          <div
            style={{
              marginLeft: "16px",
              marginTop: "29px",
            }}>
            <div>
              セキュリティを強化するために強力なパスワードを設定します。
              複雑にするために、英数字・特殊文字の組み合わせを使用してください。
            </div>
            <div
              style={{
                display: "flex",
                placeItems: "center",
                marginTop: "34px",
              }}>
              <div>
                <div>現在のパスワード</div>
                <TextInput
                  style={{ width: "350px" }}
                  id="currentPassword"
                  name="現在のパスワード"
                  type="password"
                  isRequired
                  className={isInvalid現パスワード ? "is-error" : ""}
                  setValue={set現パスワード}
                  value={現パスワード}></TextInput>
                <div style={{ marginTop: "35px" }}>新しいパスワード</div>
                <div>
                  <TextInput
                    style={{ width: "350px" }}
                    id="newPassword"
                    name="新しいパスワード"
                    type="password"
                    isRequired
                    maxLength={20}
                    className={isInvalid新パスワード ? "is-error" : ""}
                    setValue={set新パスワード}
                    value={新パスワード}></TextInput>
                  <PasswordStrengthBar
                    style={{ marginTop: "22px" }}
                    scoreWords={[]}
                    password={新パスワード}
                    shortScoreWord=""
                  />
                </div>
                <div>
                  パスワード強度:
                  {passwordScore ? パスワード強度[passwordScore] : ""}
                </div>
                <div style={{ marginTop: "13px" }}>新しいパスワード(確認)</div>
                <TextInput
                  style={{ width: "350px" }}
                  id="checkPassword"
                  name="新しいパスワード_確認"
                  className={isInvalid新パスワード_確認 ? "is-error" : ""}
                  type="password"
                  isRequired
                  setValue={setパスワード_確認}
                  value={新パスワード_確認}></TextInput>
              </div>
              <div>
                <div className="password-hint-box">
                  パスワードの強度をあげるには以下を参考にしてください。
                  <ul style={{ marginTop: "13px" }}>
                    <li>数字を含めてください。</li>
                    <li>文字数は12文字以上をおすすめします。</li>
                  </ul>
                </div>
              </div>
            </div>
            <div style={{ textAlign: "center", marginTop: "71px" }}>
              <Button
                style={{ width: "350px", height: "36px" }}
                onClick={submit}>
                登録
              </Button>
            </div>
          </div>
        </div>
      </form>
    </>
  )
}

export default Password
